import type { TOptions } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { TranslationKeys } from '../../../shared/types/locale';

// Recursively get all dot prefixes from keys
type PrefixesOf<K extends string> = K extends `${infer Head}.${infer Tail}`
	? Head | `${Head}.${PrefixesOf<Tail>}`
	: never;

// Include root (empty string) in valid prefixes
type AllValidKeyPrefixes =
	| ''
	| (TranslationKeys extends infer T extends string ? PrefixesOf<T> : never);

// If prefix is '', return all keys; otherwise, filter
type ScopedKeys<
	AllKeys extends string,
	Prefix extends string,
> = Prefix extends ''
	? AllKeys
	: AllKeys extends `${Prefix}.${infer Rest}`
		? Rest
		: never;

export type TranslationSubKey<Prefix extends AllValidKeyPrefixes> = ScopedKeys<
	TranslationKeys,
	Prefix
>;

export function useLocale<Prefix extends AllValidKeyPrefixes>(prefix: Prefix) {
	const { t: rawT } = useTranslation();

	const t = <K extends TranslationSubKey<Prefix>>(
		key: K,
		options?: TOptions
	): string => {
		return prefix
			? rawT(`${prefix}.${key}` as string, options)
			: rawT(key, options);
	};

	return { t };
}
